import { useCallback, useEffect, useMemo, useState } from "react";

import {
  factIdsBySourceReference,
  factSourceReferenceIds,
  type FactForSourceReverse,
  type ReferenceForFactSource,
} from "../lib/factSourceReferenceIds";

/** What set the current `hoveredReferenceId`. A source-CARD hover highlights that
 *  source's FACTS (never other cards); a citation-in-answer hover highlights the
 *  cited card + the answer text it backs. A fact hover uses a separate id. */
type HoverSource = "card" | "citation" | null;

export interface UseSourceFactHighlightArgs {
  /** Agent-contributed facts (AI-prefilled + human-typed). Their `sourceRefs`
   *  provenance drives the fact -> source highlight (#500). */
  facts: readonly FactForSourceReverse[] | null | undefined;
  /** Every place a grounding source can render: the pooled sources plus each
   *  to-do's own citations (mapped through `citationToReference`). Assembling the
   *  pool is app-specific data plumbing and stays with the caller. */
  referencePool: readonly ReferenceForFactSource[] | null | undefined;
  /** Surface-specific highlight contributions merged at a defined precedence
   *  (below fact/citation hover, above the click pin). Paragraph-hover feeds this
   *  seam; the hook stays agnostic to what produced it. Omit when unused. */
  extraHighlightIds?: Set<string> | null;
  /** Same seam for force-expand (auto-open a collapsed source group). */
  extraAutoExpandIds?: Set<string> | null;
  /** When this changes the click pin is cleared automatically (pass the current
   *  message / conversation id so a pinned source does not carry across tickets).
   *  Hover state is transient and needs no reset. */
  resetKey?: string | null;
}

export interface UseSourceFactHighlightResult {
  /** Source-card ids to highlight. */
  highlightedReferenceIds: Set<string> | null;
  /** Subset that should FORCE a collapsed source group open (deliberate
   *  navigation: citation hover / click pin / the extra seam). Excludes fact
   *  hover, which tints a collapsed group's header instead of popping it open. */
  autoExpandReferenceIds: Set<string> | null;
  /** Fact ids to highlight (set only while hovering a source card). */
  highlightedFactIds: Set<string> | null;
  /** The single source the answer text should treat as focused for paragraph
   *  tinting: a citation hover or the click pin, never a card hover or fact
   *  hover. Surfaces that render the answer with hoverable citations (Venus's
   *  AnnotatedAnswer) pass this down; surfaces that do not can ignore it. */
  focusedReferenceId: string | null;

  /** Hovering a fact row: highlight the sources it was drawn from. */
  onFactHover: (factId: string | null) => void;
  /** Hovering a source CARD: highlight that source's facts (never other cards). */
  onReferenceHover: (referenceId: string | null) => void;
  /** Hovering an inline citation in the answer: highlight + expand the cited
   *  card and tint the paragraph it backs. */
  onCitationHover: (referenceId: string | null) => void;
  /** Click-to-pin a source: a persistent highlight that survives subsequent
   *  hovers until `clearPin`. Side effects (open panel, scroll into view) stay
   *  with the caller; this only records the pinned id. */
  pinReference: (referenceId: string | null) => void;
  /** Clear the click pin (e.g. on message / conversation change). */
  clearPin: () => void;
}

/**
 * Source <-> fact hover highlighting for the proposed-answer surface, shared by
 * Venus (AgentPage) and Neptune (ProposedAnswerTab) so the two cannot drift
 * (this logic used to be copy-pasted into both).
 *
 * It is a pure state machine: facts + reference pool in, highlight sets out. It
 * owns the hover / pin STATE and the precedence between highlight sources; it
 * knows nothing about the DOM, panels, scrolling, or the answer renderer. Each
 * app wires its own surface events to the intent handlers and keeps its own side
 * effects. Surface-specific highlight sources (paragraph-hover) plug in through
 * `extraHighlightIds` / `extraAutoExpandIds` rather than being baked in here.
 *
 * Precedence for `highlightedReferenceIds` (most specific first):
 *   fact hover -> citation hover -> extra (e.g. paragraph) -> click pin
 * Fact hover is deliberately absent from `autoExpandReferenceIds` so hovering a
 * fact tints a collapsed group's header instead of forcing it open.
 */
export function useSourceFactHighlight({
  facts,
  referencePool,
  extraHighlightIds = null,
  extraAutoExpandIds = null,
  resetKey = null,
}: UseSourceFactHighlightArgs): UseSourceFactHighlightResult {
  const [hoveredFactId, setHoveredFactId] = useState<string | null>(null);
  const [hoveredReferenceId, setHoveredReferenceId] = useState<string | null>(
    null,
  );
  const [hoverSource, setHoverSource] = useState<HoverSource>(null);
  // Persistent highlight for a clicked source. Unlike hover (cleared on
  // mouse-leave) it survives subsequent hovers until cleared, so the clicked
  // card stays marked while the operator looks around.
  const [clickedReferenceId, setClickedReferenceId] = useState<string | null>(
    null,
  );

  // Null (not an empty Set) when the hovered fact resolves to nothing, so a fact
  // without provenance (human-typed, or a row predating provenance) falls
  // through to the citation / pin highlight rather than clearing it.
  const factHoverReferenceIds = useMemo(() => {
    if (!hoveredFactId) return null;
    const fact = facts?.find((f) => f.id === hoveredFactId);
    const ids = factSourceReferenceIds(fact?.sourceRefs, referencePool);
    return ids.size > 0 ? ids : null;
  }, [hoveredFactId, facts, referencePool]);

  // Memoized so the Set identity stays stable across renders when the inputs are
  // unchanged: this feeds the references panels and an effect in
  // CustomerRequestSourceReferences keyed on `[highlightedReferenceIds, ...]`, so an
  // unstable identity would needlessly re-run that effect on every commit.
  const highlightedReferenceIds = useMemo(
    () =>
      // Fact hover wins: the most specific gesture, and the pointer cannot rest
      // on a fact row and a citation/card at once (#500).
      factHoverReferenceIds ??
      // A source-CARD hover must NOT light up source cards (it highlights that
      // source's facts instead -- see highlightedFactIds). Only a citation
      // hover, the extra seam, or the click pin light up cards.
      (hoverSource === "citation" && hoveredReferenceId !== null
        ? new Set([hoveredReferenceId])
        : (extraHighlightIds ??
          (clickedReferenceId !== null
            ? new Set([clickedReferenceId])
            : null))),
    [
      factHoverReferenceIds,
      hoverSource,
      hoveredReferenceId,
      extraHighlightIds,
      clickedReferenceId,
    ],
  );

  const autoExpandReferenceIds = useMemo(
    () =>
      hoverSource === "citation" && hoveredReferenceId !== null
        ? new Set([hoveredReferenceId])
        : (extraAutoExpandIds ??
          (clickedReferenceId !== null ? new Set([clickedReferenceId]) : null)),
    [hoverSource, hoveredReferenceId, extraAutoExpandIds, clickedReferenceId],
  );

  // Reverse of the fact -> source map: source-card id -> the facts grounded in
  // it, so hovering a source highlights the facts written from it.
  const factIdsBySource = useMemo(
    () => factIdsBySourceReference(facts, referencePool),
    [facts, referencePool],
  );

  const highlightedFactIds = useMemo(
    () =>
      hoverSource === "card" && hoveredReferenceId !== null
        ? (factIdsBySource.get(hoveredReferenceId) ?? null)
        : null,
    [hoverSource, hoveredReferenceId, factIdsBySource],
  );

  // The source the answer text should tint: a deliberate citation hover, else
  // the click pin. A card hover (facts, not answer text) and a fact hover both
  // leave the answer untinted.
  const focusedReferenceId =
    hoverSource === "card"
      ? null
      : hoverSource === "citation"
        ? hoveredReferenceId
        : clickedReferenceId;

  const onFactHover = useCallback((factId: string | null) => {
    setHoveredFactId(factId);
  }, []);

  const onReferenceHover = useCallback((referenceId: string | null) => {
    setHoveredReferenceId(referenceId);
    setHoverSource(referenceId ? "card" : null);
  }, []);

  const onCitationHover = useCallback((referenceId: string | null) => {
    setHoveredReferenceId(referenceId);
    setHoverSource(referenceId ? "citation" : null);
  }, []);

  const pinReference = useCallback((referenceId: string | null) => {
    setClickedReferenceId(referenceId);
  }, []);

  const clearPin = useCallback(() => {
    setClickedReferenceId(null);
  }, []);

  // Drop the persistent pin when the host switches message / conversation, so a
  // source pinned on one ticket does not stay highlighted on the next.
  useEffect(() => {
    setClickedReferenceId(null);
  }, [resetKey]);

  return {
    highlightedReferenceIds,
    autoExpandReferenceIds,
    highlightedFactIds,
    focusedReferenceId,
    onFactHover,
    onReferenceHover,
    onCitationHover,
    pinReference,
    clearPin,
  };
}
